import React, { useEffect, useState } from 'react';
import classes from './RepeatRegularly.module.scss';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { TimezoneSetting } from '../../../../models/graphql';
import { mapTimezone } from '../../../../helpers/map-timezone';
import { PostEditorValidationMessages } from '../../constants/validation-messages';
import { PostOnDate } from '../PostOnDate';
import { OnUpdatePostOnTimeFunction } from '../PostOnDate/PostOnDate.view';
import { Select } from '../../../../components/forms/Select';
import formClasses from '../../../../components/forms/form.module.scss';
import { PostEditorRecurringIntervalType } from '../../../../models/post-editor';
import { Input } from '../../../../components/forms/Input';
import classNames from 'classnames';
import { InputTypes } from '../../../../constants/form';
import { FormElementAction } from '../../../../models/basic-objects';
import { ScheduleHelpers } from '../Schedule.helpers';
import inputClasses from '../../../../components/forms/form.module.scss';

export type RepeatRegularlyViewProps = {
  //
  formInstance: UseFormReturn;
  scheduledDate: string;
  groupName: string;
  onUpdate: OnUpdatePostOnTimeFunction;
  selectAction: FormElementAction;
  timeZone: TimezoneSetting;
  recurringDataType: PostEditorRecurringIntervalType;
  recurringDataValue: number;
  expandTimezoneSelector: boolean;
  updateIntervalType: FormElementAction;
  updateRecurringValue: FormElementAction<unknown>;
  postOnDateError?: string;
  postOnDateWarning?: string;
  recurringIntervalValueError?: string;
  recurringIntervalValueWarning?: string;
};

export const RepeatRegularlyView = (props: RepeatRegularlyViewProps): JSX.Element => {
  const {
    timeZone,
    scheduledDate,
    expandTimezoneSelector,
    formInstance,
    groupName,
    onUpdate,
    selectAction,
    updateIntervalType,
    updateRecurringValue,
    recurringDataType,
    recurringDataValue,
    postOnDateError,
    postOnDateWarning,
    recurringIntervalValueError,
    recurringIntervalValueWarning
  } = props;
  const [timezoneVisibility, setTimezoneVisibility] = useState<boolean>(expandTimezoneSelector);
  const timeZoneListItems = mapTimezone();
  const timezoneRules: RegisterOptions = {
    required: PostEditorValidationMessages.schedule.timezone.required
  };
  const intervalTypeRules = {};
  const intervalValueRules = {};

  const { date, time } = ScheduleHelpers.convertIsoStringDateToPostOnDate(scheduledDate);

  useEffect(() => {
    setTimezoneVisibility(expandTimezoneSelector);
  }, [expandTimezoneSelector]);

  const errorRecurringValueClasses = classNames(
    {
      [inputClasses.HelpTextErrorClass]: !!recurringIntervalValueError,
      [inputClasses.HelpTextWarningClass]: !recurringIntervalValueError && !!recurringIntervalValueWarning
    },
    inputClasses.HelpText
  );

  return (
    <>
      <div className={classes.RepeatRegularlyWrapper}>
        <PostOnDate
          {...(postOnDateError ? { error: postOnDateError } : {})}
          {...(postOnDateWarning ? { warning: postOnDateWarning } : {})}
          date={date}
          time={time}
          formInstance={formInstance}
          name={`${groupName}.postOn`}
          label={'Post on'}
          onUpdate={onUpdate}
          wrapper
        />
      </div>

      <div className={'grid-x margin-top-small-24'}>
        <div className={`cell shrink ${classes.ChangeTimezone}`}>
          <a onClick={() => setTimezoneVisibility(old => !old)}>
            {timezoneVisibility ? 'Hide Timezone' : 'Change Timezone'}
          </a>
        </div>
        {timezoneVisibility && (
          <div className={'cell small-12 margin-top-small-16'}>
            <Select
              items={timeZoneListItems}
              action={selectAction}
              selectedValue={timeZone}
              label={'Timezone'}
              name={`${groupName}Timezone`}
              size={'large'}
              rules={timezoneRules}
              useFormInstance={formInstance}
            />
          </div>
        )}
      </div>

      <div className={classNames(classes.RepeatRegularlyWrapper, 'margin-top-small-24')}>
        <label className={formClasses.Label}>And again every</label>
        <div className={'grid-x align-justify'}>
          <div
            className={classNames(classes.IntervalValueInput, classes.IntervalWrapper, {
              [classes.IntervalValueInputError]: !!recurringIntervalValueError,
              [classes.IntervalValueInputWarning]: !recurringIntervalValueError && !!recurringIntervalValueWarning
            })}>
            <Input
              value={recurringDataValue}
              updateAction={updateRecurringValue}
              updateDebounceTiming={400}
              type={InputTypes.number}
              formInstance={formInstance}
              name={`${groupName}.intervalValue`}
              rules={intervalValueRules}
              size={'large'}
            />
          </div>

          <div className={classNames(classes.IntervalSelectInput, classes.IntervalWrapper)}>
            <Select
              action={updateIntervalType}
              chooseLabel={'--'}
              items={[
                { label: 'Days', value: PostEditorRecurringIntervalType.days },
                { label: 'Weeks', value: PostEditorRecurringIntervalType.weeks },
                { label: 'Years', value: PostEditorRecurringIntervalType.years }
              ]}
              selectedValue={recurringDataType}
              name={`${groupName}.intervalType`}
              size={'large'}
              rules={intervalTypeRules}
              useFormInstance={formInstance}
            />
          </div>

          {recurringIntervalValueError || recurringIntervalValueWarning ? (
            <p className={errorRecurringValueClasses}>{recurringIntervalValueError || recurringIntervalValueWarning}</p>
          ) : null}
        </div>
      </div>
    </>
  );
};
